import { useState } from "react";
import { X, Mail, Loader2, Eye, EyeOff, ArrowLeft } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function LinkAccountModal({ onClose, defaultEmail = "" }) {
  const { upgradeGuestToGoogle, upgradeGuestToEmail } = useAuth();
  const [mode, setMode] = useState("options"); // options, email
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState(defaultEmail);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [touched, setTouched] = useState({});

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const fieldError = (field) => {
    if (!touched[field]) return null;
    if (field === "email" && email && !isValidEmail) return "Ingresa un correo válido";
    if (field === "email" && !email) return "El correo es obligatorio";
    if (field === "password" && password && password.length < 8) return "Mínimo 8 caracteres";
    if (field === "password" && !password) return "La contraseña es obligatoria";
    if (field === "confirm" && confirmPassword && password !== confirmPassword) return "Las contraseñas no coinciden";
    if (field === "confirm" && !confirmPassword) return "Confirma tu contraseña";
    return null;
  };

  const blur = (field) => setTouched((t) => ({ ...t, [field]: true }));

  const handleGoogleLink = async () => {
    setLoading(true);
    setError(null);
    const result = await upgradeGuestToGoogle();
    if (result.success) {
      onClose();
    } else if (result.error) {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleEmailLink = async () => {
    setError(null);
    // Touch all fields to show any remaining errors
    setTouched({ email: true, password: true, confirm: true });
    
    if (!email || !isValidEmail) return;
    if (password.length < 8) return;
    if (password !== confirmPassword) return;

    setLoading(true);
    const result = await upgradeGuestToEmail(email, password);
    if (result.success) {
      onClose();
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.5)" }}
      onClick={onClose}
    >
      <div 
        className="we-card p-6 w-full max-w-[360px] relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 cursor-pointer"
          style={{ background: "transparent", border: "none", color: "#71644C" }}
        >
          <X size={20} />
        </button>

        {mode === "options" ? (
          <>
            <h3 className="text-lg font-bold mb-2" style={{ color: "#2D2A24" }}>
              Vincular tu cuenta
            </h3>
            <p className="text-xs mb-5" style={{ color: "#71644C" }}>
              Guarda tu progreso creando una cuenta permanente
            </p>

            {error && (
              <div 
                className="rounded-xl p-3 mb-4 text-sm"
                style={{ background: "#FDECEA", color: "#C62828", border: "1px solid #FFCDD2" }}
              >
                {error}
              </div>
            )}

            {/* Google option */}
            <button
              onClick={handleGoogleLink}
              disabled={loading}
              className="w-full cursor-pointer active:scale-[0.97] transition-transform flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed mb-3"
              style={{
                padding: 14,
                borderRadius: 12,
                border: "1.5px solid #E2D9CB",
                background: "#FFFFFF",
                fontSize: 14,
                fontWeight: 600,
                color: "#2D2A24",
              }}
            >
              {loading ? (
                <Loader2 size={20} className="animate-spin" />
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              )}
              <span>Vincular con Google</span>
            </button>

            {/* Email option */}
            <button
              onClick={() => setMode("email")}
              disabled={loading}
              className="w-full cursor-pointer active:scale-[0.97] transition-transform flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                padding: 14,
                borderRadius: 12,
                border: "1.5px solid #E2D9CB",
                background: "#FFFFFF",
                fontSize: 14,
                fontWeight: 600,
                color: "#2D2A24",
              }}
            >
              <Mail size={20} style={{ color: "#71644C" }} />
              <span>Vincular con email</span>
            </button>
          </>
        ) : (
          <>
            <button
              onClick={() => { setMode("options"); setError(null); }}
              className="flex items-center gap-1 cursor-pointer mb-4"
              style={{ background: "transparent", border: "none", color: "#71644C", fontSize: 13 }}
            >
              <ArrowLeft size={14} />
              <span>Volver</span>
            </button>

            <h3 className="text-lg font-bold mb-2" style={{ color: "#2D2A24" }}>
              Vincular con email
            </h3>
            <p className="text-xs mb-4" style={{ color: "#71644C" }}>
              Crea tu cuenta con correo y contraseña
            </p>

            {error && (
              <div 
                className="rounded-xl p-3 mb-4 text-sm"
                style={{ background: "#FDECEA", color: "#C62828", border: "1px solid #FFCDD2" }}
              >
                {error}
              </div>
            )}

            <div style={{ position: "relative", marginBottom: 22 }}>
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(null); }}
                onBlur={() => blur("email")}
                placeholder="Correo electrónico"
                className={`we-input w-full ${fieldError("email") ? "we-input-error" : ""}`}
                data-testid="link-email-input"
              />
              {fieldError("email") && (
                <p style={{ position: "absolute", bottom: -14, left: 4, fontSize: 11, lineHeight: 1, color: "#C0453A", fontWeight: 500, pointerEvents: "none" }}>
                  {fieldError("email")}
                </p>
              )}
            </div>

            <div style={{ position: "relative", marginBottom: 22 }}>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(null); }}
                  onBlur={() => blur("password")}
                  placeholder="Contraseña (mínimo 8 caracteres)"
                  className={`we-input w-full pr-10 ${fieldError("password") ? "we-input-error" : ""}`}
                  data-testid="link-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer"
                  style={{ background: "transparent", border: "none", color: "#71644C" }}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {fieldError("password") && (
                <p style={{ position: "absolute", bottom: -14, left: 4, fontSize: 11, lineHeight: 1, color: "#C0453A", fontWeight: 500, pointerEvents: "none" }}>
                  {fieldError("password")}
                </p>
              )}
            </div>

            <div style={{ position: "relative", marginBottom: 22 }}>
              <input
                type={showPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setError(null); }}
                onBlur={() => blur("confirm")}
                placeholder="Confirmar contraseña"
                className={`we-input w-full ${fieldError("confirm") ? "we-input-error" : ""}`}
                data-testid="link-confirm-password-input"
              />
              {fieldError("confirm") && (
                <p style={{ position: "absolute", bottom: -14, left: 4, fontSize: 11, lineHeight: 1, color: "#C0453A", fontWeight: 500, pointerEvents: "none" }}>
                  {fieldError("confirm")}
                </p>
              )}
            </div>

            <button
              onClick={handleEmailLink}
              disabled={loading || !email || !password || !confirmPassword}
              className="w-full cursor-pointer active:scale-[0.97] transition-transform flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                padding: 14,
                borderRadius: 12,
                background: (email && password && confirmPassword) 
                  ? "linear-gradient(135deg,#00628F 0%,#0080A0 50%,#2A9D8F 100%)" 
                  : "#B5A998",
                color: "white",
                fontSize: 14,
                fontWeight: 600,
                border: "none",
              }}
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : null}
              <span>{loading ? "Vinculando..." : "Vincular cuenta"}</span>
            </button>
          </>
        )}
      </div>
    </div>
  );
}
